import { useState } from "react";
import { WORLDS, TIER_WORLDS } from "../../data/worlds";
import { getHighestBadge } from "../../data/streakBadges";
import { getTodaysTip, getTipColors, CAT_LABELS } from "../../data/tips";

const TIER_META: Record<string, { heading: string; sub: string; ageLabel: string }> = {
  starter: { heading: "Money Adventure! 💰", sub: "Complete both worlds to level up to Money Builder! 🚀", ageLabel: "Ages 10–12" },
  builder: { heading: "Money Builder",        sub: "Complete both worlds to reach Money Master",            ageLabel: "Ages 13–15" },
  master:  { heading: "Money Master",         sub: "Complete all worlds to master your finances",           ageLabel: "Ages 16–18" },
};

function getTierConfig(tier: string) {
  if (tier === "master") return {
    dark: true,
    showMascot: false,
    animateMascot: false,
    animateWorldEmoji: false,
    mascotSize: 0,
    worldEmojiSize: 36,
    cardBorderWidth: 2,
    cardBorderRadius: 18,
    showWelcomeTo: false,
    headingSize: 24,
  };
  if (tier === "builder") return {
    dark: false,
    showMascot: true,
    animateMascot: false,
    animateWorldEmoji: false,
    mascotSize: 32,
    worldEmojiSize: 36,
    cardBorderWidth: 2,
    cardBorderRadius: 18,
    showWelcomeTo: false,
    headingSize: 26,
  };
  return {
    dark: false,
    showMascot: true,
    animateMascot: true,
    animateWorldEmoji: true,
    mascotSize: 56,
    worldEmojiSize: 46,
    cardBorderWidth: 3,
    cardBorderRadius: 22,
    showWelcomeTo: true,
    headingSize: 32,
  };
}

function getWorldLocked(worlds: any[], idx: number, progress: Record<string, boolean>): boolean {
  if (idx === 0) return false;
  const prev = worlds[idx - 1];
  const threshold: number = prev.unlockThreshold ?? prev.lessons.length;
  const done = prev.lessons.filter((_: any, i: number) => progress[`${prev.id}-${i}`]).length;
  return done < threshold;
}

function lockHint(worlds: any[], idx: number, progress: Record<string, boolean>): string {
  if (idx === 0) return "";
  const prev = worlds[idx - 1];
  const threshold: number = prev.unlockThreshold ?? prev.lessons.length;
  const done = prev.lessons.filter((_: any, i: number) => progress[`${prev.id}-${i}`]).length;
  return `${prev.label}: ${done}/${prev.lessons.length} lessons (${threshold} to unlock)`;
}

function getTodaysQuestion(worlds: any[]) {
  const pool: { key: string; q: any }[] = [];
  for (const w of worlds) {
    for (let li = 0; li < w.lessons.length; li++) {
      for (let qi = 0; qi < w.lessons[li].quiz.length; qi++) {
        pool.push({ key: `${w.id}-${li}-${qi}`, q: w.lessons[li].quiz[qi] });
      }
    }
  }
  if (pool.length === 0) return null;
  const d = new Date().toISOString().slice(0, 10).replace(/-/g, "");
  return pool[parseInt(d) % pool.length];
}

function DailyChallengeCard({
  question, streak, earnedBadges, onComplete, dark,
}: {
  question: any; streak: number; earnedBadges: string[];
  onComplete: (correct: boolean) => void; dark?: boolean;
}) {
  const [selected, setSelected] = useState<number | null>(null);
  const [answered, setAnswered] = useState(false);

  if (!question) return null;
  const { q } = question;
  const badge = getHighestBadge(earnedBadges);

  function pick(idx: number) {
    if (answered) return;
    setSelected(idx);
    setAnswered(true);
    onComplete(idx === q.a);
  }

  const correct = answered && selected === q.a;

  if (dark) {
    return (
      <div style={{ background: "#1e293b", borderRadius: 18, border: "2px solid #fbbf2433", boxShadow: "0 4px 16px rgba(251,191,36,0.08)", overflow: "hidden", marginBottom: 2 }}>
        <div style={{ background: "linear-gradient(135deg,#78350f,#451a03)", padding: "10px 14px", display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ fontSize: 18, flexShrink: 0 }}>🔥</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#fbbf24", letterSpacing: 2, textTransform: "uppercase" as const }}>Daily Challenge</div>
            <div style={{ fontSize: 11, color: "#fcd34d88", fontWeight: 600 }}>
              {streak > 0 ? `${streak}-day streak — keep it going` : "Start your streak today"}
            </div>
          </div>
          <div style={{ textAlign: "right" as const }}>
            {streak > 0 && <div style={{ fontSize: 15, fontWeight: 700, color: "#fbbf24" }}>{streak}🔥</div>}
            {badge && <div style={{ fontSize: 13 }}>{badge.emoji}</div>}
          </div>
        </div>
        <div style={{ padding: "12px 14px 14px" }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#f1f5f9", marginBottom: 10, lineHeight: 1.45 }}>{q.q}</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            {q.opts.map((opt: string, i: number) => {
              let bg = "#0f172a", border = "#334155", color = "#94a3b8";
              if (answered) {
                if (i === q.a)           { bg = "#052e16"; border = "#16a34a"; color = "#4ade80"; }
                else if (i === selected) { bg = "#450a0a"; border = "#dc2626"; color = "#f87171"; }
              } else if (selected === i) { bg = "#1e1b4b"; border = "#6366f1"; color = "#a5b4fc"; }
              return (
                <button key={i} onClick={() => pick(i)} disabled={answered}
                  style={{ background: bg, border: `1.5px solid ${border}`, borderRadius: 10, padding: "8px 12px", textAlign: "left", fontSize: 13, fontWeight: 600, color, cursor: answered ? "default" : "pointer", fontFamily: "Nunito", transition: "all 0.15s" }}>
                  {opt}
                </button>
              );
            })}
          </div>
          {answered && (
            <div style={{ marginTop: 10, padding: "8px 12px", borderRadius: 10, background: correct ? "#052e16" : "#1a1200", fontSize: 13, fontWeight: 600, color: correct ? "#4ade80" : "#fbbf24" }}>
              {correct ? `✅ ${q.praise}` : `💡 Answer: ${q.opts[q.a]}`}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: "#fff", borderRadius: 20, border: "2.5px solid #fbbf24", boxShadow: "0 4px 20px #fbbf2422", overflow: "hidden", marginBottom: 2 }}>
      <div style={{ background: "linear-gradient(135deg,#fbbf24,#f97316)", padding: "10px 14px", display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ fontSize: 24, animation: "bounce 2s infinite" }}>🔥</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 900, color: "#fff", fontFamily: "Fredoka One" }}>Daily Challenge</div>
          <div style={{ fontSize: 11, color: "#fff9", fontWeight: 700 }}>{streak > 0 ? `🔥 ${streak} day streak — keep it going!` : "Start your streak today!"}</div>
        </div>
        <div style={{ textAlign: "right" as const }}>
          <div style={{ fontSize: 22, fontWeight: 900, color: "#fff", fontFamily: "Fredoka One" }}>{streak > 0 ? `${streak}🔥` : ""}</div>
          {badge && <div style={{ fontSize: 14 }}>{badge.emoji}</div>}
        </div>
      </div>
      <div style={{ padding: "12px 14px 14px" }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: "#1f2937", marginBottom: 10, lineHeight: 1.4 }}>{q.q}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
          {q.opts.map((opt: string, i: number) => {
            let bg = "#f9fafb", border = "#e5e7eb", color = "#374151";
            if (answered) {
              if (i === q.a)           { bg = "#dcfce7"; border = "#16a34a"; color = "#15803d"; }
              else if (i === selected) { bg = "#fee2e2"; border = "#dc2626"; color = "#991b1b"; }
            } else if (selected === i) { bg = "#fff7ed"; border = "#f97316"; }
            return (
              <button key={i} onClick={() => pick(i)} disabled={answered}
                style={{ background: bg, border: `2px solid ${border}`, borderRadius: 10, padding: "8px 12px", textAlign: "left", fontSize: 13, fontWeight: 800, color, cursor: answered ? "default" : "pointer", fontFamily: "Nunito", transition: "all 0.15s" }}>
                {opt}
              </button>
            );
          })}
        </div>
        {answered && (
          <div style={{ marginTop: 10, padding: "8px 12px", borderRadius: 10, background: correct ? "#dcfce7" : "#fef9c3", fontSize: 13, fontWeight: 800, color: correct ? "#15803d" : "#713f12" }}>
            {correct ? `✅ ${q.praise}` : `💡 The answer was: ${q.opts[q.a]}`}
          </div>
        )}
      </div>
    </div>
  );
}

export default function WorldMap({ progress, tier, onSelect, dailyData, onChallengeComplete, dailyEvent, onDismissDailyEvent, heroGradient, equippedTitle }: any) {
  const tierIds = TIER_WORLDS[tier] ?? [];
  const worlds  = WORLDS.filter(w => tierIds.includes(w.id));
  const totalLessons = worlds.reduce((s, w) => s + w.lessons.length, 0);
  const done = worlds.reduce(
    (count, w) => count + w.lessons.filter((_, i) => progress[`${w.id}-${i}`]).length,
    0
  );

  const tc = getTierConfig(tier);
  const { heading, sub } = TIER_META[tier] ?? TIER_META.starter;
  const tierMascot     = tier !== "master" ? "🐿️" : "";
  const tierMascotName = tier !== "master" ? "Stash" : "";

  const today        = new Date().toISOString().slice(0, 10);
  const doneToday    = dailyData?.lastCompleted === today;
  const todaysQ      = !doneToday ? getTodaysQuestion(worlds) : null;
  const earnedBadges: string[] = dailyData?.earnedBadges ?? [];
  const highestBadge = getHighestBadge(earnedBadges);

  const todaysTip = getTodaysTip();
  const tipColors = getTipColors(todaysTip.category);
  const [tipDismissed, setTipDismissed] = useState<boolean>(() => {
    try { return localStorage.getItem("ms_tip_dismissed_v1") === today; } catch { return false; }
  });
  function dismissTip() {
    try { localStorage.setItem("ms_tip_dismissed_v1", today); } catch { /* quota */ }
    setTipDismissed(true);
  }

  /* ─────────────────────────────────────────
     MONEY MASTER — dark / pro-adjacent layout
  ───────────────────────────────────────── */
  if (tier === "master") {
    return (
      <div style={{ fontFamily: "Nunito", paddingBottom: 100, background: "#0f172a", minHeight: "100vh" }}>

        {/* Tip of the Day — dark */}
        {!tipDismissed && (
          <div style={{ background: "#1e293b", borderBottom: "1px solid #334155", padding: "10px 16px", display: "flex", alignItems: "flex-start", gap: 10 }}>
            <div style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>{todaysTip.emoji}</div>
            <div style={{ flex: 1 }}>
              <div style={{ marginBottom: 3 }}>
                <span style={{ background: "#334155", borderRadius: 20, padding: "1px 8px", fontSize: 10, fontWeight: 700, color: "#94a3b8", letterSpacing: 0.5 }}>
                  {CAT_LABELS[todaysTip.category]}
                </span>
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#cbd5e1", lineHeight: 1.45 }}>{todaysTip.tip}</div>
            </div>
            <button onClick={dismissTip} style={{ background: "none", border: "none", fontSize: 16, color: "#475569", cursor: "pointer", padding: "0 2px", flexShrink: 0, lineHeight: 1 }}>✕</button>
          </div>
        )}

        {/* Hero — dark */}
        <div style={{ background: "linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%)", padding: "24px 18px 20px", borderBottom: "1px solid #1e293b", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: -20, right: -10, fontSize: 90, opacity: 0.06 }}>🏆</div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", letterSpacing: 3, textTransform: "uppercase", marginBottom: 8 }}>
            Money Master · Ages 16–18
          </div>
          <div style={{ fontSize: 26, fontWeight: 900, color: "#f1f5f9", fontFamily: "Fredoka One", lineHeight: 1, marginBottom: 4 }}>
            Money Master
          </div>
          {equippedTitle && (
            <div style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "rgba(99,102,241,0.2)", border: "1px solid rgba(99,102,241,0.3)", borderRadius: 20, padding: "3px 10px", marginBottom: 8 }}>
              <span style={{ fontSize: 12 }}>{equippedTitle.emoji}</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: "#a5b4fc" }}>{equippedTitle.name}</span>
            </div>
          )}
          <div style={{ fontSize: 13, color: "#475569", fontWeight: 600, marginBottom: 16 }}>{sub}</div>
          <div style={{ background: "#1e293b", borderRadius: 20, height: 7, overflow: "hidden", marginBottom: 6 }}>
            <div style={{ height: "100%", background: "linear-gradient(90deg, #6366f1, #8b5cf6)", borderRadius: 20, width: `${totalLessons > 0 ? (done / totalLessons) * 100 : 0}%`, transition: "width 0.7s" }} />
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ fontSize: 12, color: "#475569", fontWeight: 600 }}>{done} of {totalLessons} lessons complete</div>
            {highestBadge && (
              <div style={{ background: "#1e293b", borderRadius: 20, padding: "3px 10px", display: "flex", alignItems: "center", gap: 5 }}>
                <span style={{ fontSize: 13 }}>{highestBadge.emoji}</span>
                <span style={{ fontSize: 10, fontWeight: 700, color: "#64748b" }}>{highestBadge.label}</span>
              </div>
            )}
          </div>
        </div>

        <div style={{ padding: "14px 16px 0", display: "flex", flexDirection: "column", gap: 12 }}>

          {/* Daily event cards — dark variants */}
          {dailyEvent?.type === "broken" && (
            <div style={{ background: "#1a0800", border: "2px solid #fb923c33", borderRadius: 18, padding: "14px 16px" }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 10 }}>
                <div style={{ fontSize: 26 }}>💔</div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#fb923c", letterSpacing: 0.5 }}>Streak broken</div>
                  <div style={{ fontSize: 13, color: "#9a5318", fontWeight: 600, marginTop: 2 }}>
                    You had a {dailyEvent.streak}-day streak. Start a new one today.
                  </div>
                </div>
              </div>
              <button onClick={onDismissDailyEvent}
                style={{ width: "100%", background: "linear-gradient(135deg,#9a3412,#7c2d12)", border: "none", borderRadius: 10, padding: "9px", color: "#fdba74", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "Nunito" }}>
                Let's go
              </button>
            </div>
          )}

          {dailyEvent?.type === "shielded" && (
            <div style={{ background: "#052e16", border: "2px solid #16a34a33", borderRadius: 18, padding: "14px 16px" }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 10 }}>
                <div style={{ fontSize: 26 }}>🛡️</div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#4ade80", letterSpacing: 0.5 }}>Streak shield activated</div>
                  <div style={{ fontSize: 13, color: "#166534", fontWeight: 600, marginTop: 2 }}>
                    Your {dailyEvent.streak}-day streak is safe. Shield recharges next week.
                  </div>
                </div>
              </div>
              <button onClick={onDismissDailyEvent}
                style={{ width: "100%", background: "linear-gradient(135deg,#15803d,#14532d)", border: "none", borderRadius: 10, padding: "9px", color: "#86efac", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "Nunito" }}>
                Got it 🛡️
              </button>
            </div>
          )}

          {dailyEvent?.type === "milestone" && (
            <div style={{ background: "#1a1200", border: "2px solid #fbbf2433", borderRadius: 18, padding: "14px 16px" }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 10 }}>
                <div style={{ fontSize: 30 }}>{dailyEvent.badgeEmoji}</div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#fbbf24", letterSpacing: 0.5 }}>
                    {dailyEvent.streak}-day streak milestone
                  </div>
                  <div style={{ fontSize: 13, color: "#92400e", fontWeight: 600, marginTop: 2 }}>
                    You earned the <strong style={{ color: "#fcd34d" }}>{dailyEvent.badgeLabel}</strong> badge.
                  </div>
                </div>
              </div>
              <button onClick={onDismissDailyEvent}
                style={{ width: "100%", background: "linear-gradient(135deg,#92400e,#78350f)", border: "none", borderRadius: 10, padding: "9px", color: "#fcd34d", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "Nunito" }}>
                Keep going 🏆
              </button>
            </div>
          )}

          {/* Daily challenge done — dark */}
          {doneToday ? (
            <div style={{ background: "#052e16", border: "2px solid #16a34a44", borderRadius: 18, padding: "12px 16px", display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ fontSize: 26 }}>✅</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#4ade80", letterSpacing: 0.3 }}>Daily Challenge Complete</div>
                <div style={{ fontSize: 12, color: "#166534", fontWeight: 600 }}>
                  {dailyData.streak}-day streak — back tomorrow
                </div>
              </div>
              {highestBadge && <div style={{ fontSize: 20 }}>{highestBadge.emoji}</div>}
            </div>
          ) : (
            todaysQ && <DailyChallengeCard question={todaysQ} streak={dailyData?.streak ?? 0} earnedBadges={earnedBadges} onComplete={onChallengeComplete} dark />
          )}

          {/* World cards — dark / pro-style */}
          {worlds.map((w, idx) => {
            const doneCount = w.lessons.filter((_: any, i: number) => progress[`${w.id}-${i}`]).length;
            const pct = doneCount / w.lessons.length;
            const complete = pct === 1;
            const locked = getWorldLocked(worlds, idx, progress);
            return (
              <button key={w.id}
                onClick={() => !locked && onSelect(w.id)}
                className={locked ? undefined : "btn-bounce"}
                style={{
                  background: locked ? "#0f172a" : "#1e293b",
                  border: `2px solid ${locked ? "#1e293b" : w.color + "33"}`,
                  borderRadius: 18, padding: 0,
                  cursor: locked ? "default" : "pointer",
                  textAlign: "left", overflow: "hidden",
                  boxShadow: locked ? "none" : `0 4px 16px ${w.color}18`,
                  transition: "box-shadow 0.2s, transform 0.2s",
                  opacity: locked ? 0.6 : 1,
                }}>
                <div style={{ padding: "14px 16px" }}>
                  <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
                    <div style={{ fontSize: 36, lineHeight: 1, filter: locked ? "grayscale(1)" : "none" }}>{w.emoji}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: locked ? 6 : 4 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: locked ? "#334155" : w.color, letterSpacing: 1.5, textTransform: "uppercase" as const }}>{locked ? "Locked" : w.tagline}</div>
                        <div style={{ fontSize: 18 }}>{locked ? "🔒" : complete ? "⭐" : ""}</div>
                      </div>
                      <div style={{ fontSize: 17, fontWeight: 900, color: locked ? "#475569" : "#f1f5f9", fontFamily: "Fredoka One", marginBottom: locked ? 4 : 7 }}>{w.label}</div>
                      {locked ? (
                        <div style={{ fontSize: 12, color: "#475569", fontWeight: 600 }}>
                          {lockHint(worlds, idx, progress)}
                        </div>
                      ) : (
                        <>
                          <div style={{ background: "#0f172a", borderRadius: 20, height: 7, overflow: "hidden" }}>
                            <div style={{ width: `${pct * 100}%`, height: "100%", background: `linear-gradient(90deg,${w.color},${w.dark})`, borderRadius: 20, transition: "width 0.6s" }} />
                          </div>
                          <div style={{ marginTop: 4, fontSize: 11, color: w.color, fontWeight: 700 }}>{doneCount} of {w.lessons.length} lessons</div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  /* ─────────────────────────────────────────
     MONEY BUILDER — structured teen layout
  ───────────────────────────────────────── */
  if (tier === "builder") {
    return (
      <div style={{ fontFamily: "Nunito", paddingBottom: 100 }}>

        {/* Tip of the Day — light (unchanged) */}
        {!tipDismissed && (
          <div style={{ background: tipColors.bg, borderBottom: `2.5px solid ${tipColors.border}`, padding: "10px 16px", display: "flex", alignItems: "flex-start", gap: 10 }}>
            <div style={{ fontSize: 20, flexShrink: 0, marginTop: 1 }}>{todaysTip.emoji}</div>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                <div style={{ background: tipColors.tag, borderRadius: 20, padding: "1px 8px", fontSize: 10, fontWeight: 900, color: tipColors.tagText }}>
                  {CAT_LABELS[todaysTip.category]}
                </div>
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: tipColors.text, lineHeight: 1.45 }}>{todaysTip.tip}</div>
            </div>
            <button onClick={dismissTip} style={{ background: "none", border: "none", fontSize: 16, color: tipColors.text, opacity: 0.5, cursor: "pointer", padding: "0 2px", flexShrink: 0, lineHeight: 1 }}>✕</button>
          </div>
        )}

        {/* Hero — builder (structured, no float animation) */}
        <div style={{ background: heroGradient ?? "linear-gradient(135deg,#f97316 0%,#f59e0b 100%)", padding: "22px 18px 20px", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: -25, right: -15, fontSize: 100, opacity: 0.07, transform: "rotate(10deg)" }}>🚀</div>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
            {tierMascot && (
              <div style={{ fontSize: tc.mascotSize, lineHeight: 1, flexShrink: 0, marginTop: 6 }}>{tierMascot}</div>
            )}
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#ffffff99", letterSpacing: 2.5, textTransform: "uppercase" as const, marginBottom: 5 }}>
                Money Builder · Ages 13–15
              </div>
              <div style={{ fontSize: 26, fontWeight: 900, color: "#fff", fontFamily: "Fredoka One", lineHeight: 1, marginBottom: 6 }}>{heading}</div>
              {equippedTitle && (
                <div style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "#ffffff22", borderRadius: 20, padding: "3px 10px", marginBottom: 8 }}>
                  <span style={{ fontSize: 12 }}>{equippedTitle.emoji}</span>
                  <span style={{ fontSize: 11, fontWeight: 800, color: "#fff" }}>{equippedTitle.name}</span>
                </div>
              )}
              <div style={{ fontSize: 13, color: "#ffffffcc", fontWeight: 600, marginBottom: 14 }}>{sub}</div>
            </div>
          </div>
          <div style={{ background: "#ffffff30", borderRadius: 20, height: 10, overflow: "hidden", marginBottom: 5 }}>
            <div style={{ height: "100%", background: "#fff", borderRadius: 20, width: `${totalLessons > 0 ? (done / totalLessons) * 100 : 0}%`, transition: "width 0.7s" }} />
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ fontSize: 12, color: "#ffffffcc", fontWeight: 700 }}>{done} of {totalLessons} lessons complete</div>
            {highestBadge && (
              <div style={{ background: "#ffffff28", borderRadius: 20, padding: "3px 10px", display: "flex", alignItems: "center", gap: 5 }}>
                <span style={{ fontSize: 13 }}>{highestBadge.emoji}</span>
                <span style={{ fontSize: 10, fontWeight: 900, color: "#fff" }}>{highestBadge.label}</span>
              </div>
            )}
          </div>
        </div>

        <div style={{ padding: "14px 16px 0", display: "flex", flexDirection: "column", gap: 12 }}>

          {/* Daily event cards — same light style, no changes needed */}
          {dailyEvent?.type === "broken" && (
            <div style={{ background: "#fff7ed", border: "2.5px solid #fb923c", borderRadius: 18, padding: "14px 16px" }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 10 }}>
                <div style={{ fontSize: 26 }}>💔</div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 900, color: "#9a3412", fontFamily: "Fredoka One" }}>Streak broken</div>
                  <div style={{ fontSize: 13, color: "#c2410c", fontWeight: 700, marginTop: 2 }}>
                    You had a {dailyEvent.streak}-day streak! That's still great — start a new one today 💪
                  </div>
                </div>
              </div>
              {tierMascot && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, background: "#fff", borderRadius: 12, padding: "8px 12px", marginBottom: 10, border: "1.5px solid #fb923c33" }}>
                  <div style={{ fontSize: 26, flexShrink: 0, animation: "float 3s infinite" }}>{tierMascot}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#9a3412", lineHeight: 1.4 }}>
                    <span style={{ fontWeight: 900 }}>{tierMascotName} says:</span> {dailyEvent.streak} days is amazing! You can do this again — let's go! 🔥
                  </div>
                </div>
              )}
              <button onClick={onDismissDailyEvent}
                style={{ width: "100%", background: "linear-gradient(135deg,#fb923c,#ea580c)", border: "none", borderRadius: 12, padding: "9px", color: "#fff", fontWeight: 900, fontSize: 13, cursor: "pointer", fontFamily: "Nunito" }}>
                Let's go! 🔥
              </button>
            </div>
          )}

          {dailyEvent?.type === "shielded" && (
            <div style={{ background: "#f0fdf4", border: "2.5px solid #16a34a", borderRadius: 18, padding: "14px 16px" }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 10 }}>
                <div style={{ fontSize: 28 }}>🛡️</div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 900, color: "#14532d", fontFamily: "Fredoka One" }}>Streak shield activated!</div>
                  <div style={{ fontSize: 13, color: "#166534", fontWeight: 700, marginTop: 2 }}>
                    Your {dailyEvent.streak}-day streak is safe! You get one free miss per week — shield recharges next week.
                  </div>
                </div>
              </div>
              {tierMascot && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, background: "#fff", borderRadius: 12, padding: "8px 12px", marginBottom: 10, border: "1.5px solid #16a34a33" }}>
                  <div style={{ fontSize: 26, flexShrink: 0, animation: "float 3s infinite" }}>{tierMascot}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#14532d", lineHeight: 1.4 }}>
                    <span style={{ fontWeight: 900 }}>{tierMascotName} says:</span> Phew! Your streak is protected — now keep it going! 🛡️✨
                  </div>
                </div>
              )}
              <button onClick={onDismissDailyEvent}
                style={{ width: "100%", background: "linear-gradient(135deg,#16a34a,#15803d)", border: "none", borderRadius: 12, padding: "9px", color: "#fff", fontWeight: 900, fontSize: 13, cursor: "pointer", fontFamily: "Nunito" }}>
                Nice! 🛡️
              </button>
            </div>
          )}

          {dailyEvent?.type === "milestone" && (
            <div style={{ background: "linear-gradient(135deg,#fef9c3,#fef3c7)", border: "2.5px solid #fbbf24", borderRadius: 18, padding: "14px 16px" }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 10 }}>
                <div style={{ fontSize: 32 }}>{dailyEvent.badgeEmoji}</div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 900, color: "#78350f", fontFamily: "Fredoka One" }}>
                    {dailyEvent.streak}-day streak milestone! 🎉
                  </div>
                  <div style={{ fontSize: 13, color: "#92400e", fontWeight: 700, marginTop: 2 }}>
                    You earned the <strong>{dailyEvent.badgeLabel}</strong> badge. Keep it going!
                  </div>
                </div>
              </div>
              {tierMascot && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, background: "#fff", borderRadius: 12, padding: "8px 12px", marginBottom: 10, border: "1.5px solid #fbbf2444" }}>
                  <div style={{ fontSize: 30, flexShrink: 0, animation: "bounce 0.8s 3" }}>{tierMascot}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#78350f", lineHeight: 1.4 }}>
                    <span style={{ fontWeight: 900 }}>{tierMascotName} says:</span> WOW! {dailyEvent.streak} days?! You're absolutely incredible! 🏆🌟
                  </div>
                </div>
              )}
              <button onClick={onDismissDailyEvent}
                style={{ width: "100%", background: "linear-gradient(135deg,#fbbf24,#f97316)", border: "none", borderRadius: 12, padding: "9px", color: "#fff", fontWeight: 900, fontSize: 13, cursor: "pointer", fontFamily: "Nunito" }}>
                Awesome! 🏆
              </button>
            </div>
          )}

          {/* Daily challenge — builder (light, no fire bounce) */}
          {doneToday ? (
            <div style={{ background: "#dcfce7", border: "2.5px solid #16a34a", borderRadius: 18, padding: "12px 16px", display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ fontSize: 28 }}>✅</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 900, color: "#15803d", fontFamily: "Fredoka One" }}>Daily Challenge Done!</div>
                <div style={{ fontSize: 12, color: "#166534", fontWeight: 700 }}>
                  🔥 {dailyData.streak} day streak — come back tomorrow!
                </div>
              </div>
              {highestBadge && <div style={{ fontSize: 22 }}>{highestBadge.emoji}</div>}
            </div>
          ) : (
            todaysQ && <DailyChallengeCard question={todaysQ} streak={dailyData?.streak ?? 0} earnedBadges={earnedBadges} onComplete={onChallengeComplete} />
          )}

          {/* World cards — builder (tighter, no float animation) */}
          {worlds.map((w, idx) => {
            const doneCount = w.lessons.filter((_: any, i: number) => progress[`${w.id}-${i}`]).length;
            const pct = doneCount / w.lessons.length;
            const complete = pct === 1;
            const locked = getWorldLocked(worlds, idx, progress);
            return (
              <button key={w.id}
                onClick={() => !locked && onSelect(w.id)}
                className={locked ? undefined : "btn-bounce"}
                style={{
                  background: locked ? "#f1f5f9" : "#fff",
                  border: `2px solid ${locked ? "#cbd5e1" : w.color}`,
                  borderRadius: 18, padding: 0,
                  cursor: locked ? "default" : "pointer",
                  textAlign: "left", overflow: "hidden",
                  boxShadow: locked ? "none" : `0 4px 16px ${w.color}22`,
                  transition: "box-shadow 0.2s, transform 0.2s",
                  opacity: locked ? 0.65 : 1,
                }}>
                <div style={{ background: locked ? "#f8fafc" : `linear-gradient(135deg,${w.color}12,${w.bg})`, padding: "14px 16px" }}>
                  <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
                    <div style={{ fontSize: 36, lineHeight: 1, filter: locked ? "grayscale(1)" : "none" }}>{w.emoji}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
                        <div style={{ fontSize: 18, fontWeight: 900, color: locked ? "#94a3b8" : w.dark, fontFamily: "Fredoka One" }}>{w.label}</div>
                        <div style={{ fontSize: 20 }}>{locked ? "🔒" : complete ? "⭐" : ""}</div>
                      </div>
                      {locked ? (
                        <div style={{ fontSize: 12, color: "#94a3b8", fontWeight: 700 }}>
                          {lockHint(worlds, idx, progress)}
                        </div>
                      ) : (
                        <>
                          <div style={{ fontSize: 12, color: w.color, fontWeight: 700, marginBottom: 8 }}>{w.tagline}</div>
                          <div style={{ background: "#e5e7eb", borderRadius: 20, height: 8, overflow: "hidden" }}>
                            <div style={{ width: `${pct * 100}%`, height: "100%", background: `linear-gradient(90deg,${w.color},${w.dark})`, borderRadius: 20, transition: "width 0.6s" }} />
                          </div>
                          <div style={{ marginTop: 4, fontSize: 11, color: w.color, fontWeight: 700 }}>{doneCount} of {w.lessons.length} lessons</div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  /* ─────────────────────────────────────────
     MONEY STARTER — playful kids layout (unchanged)
  ───────────────────────────────────────── */
  return (
    <div style={{ fontFamily: "Nunito", paddingBottom: 100 }}>

      {/* Tip of the Day */}
      {!tipDismissed && (
        <div style={{ background: tipColors.bg, borderBottom: `2.5px solid ${tipColors.border}`, padding: "10px 16px", display: "flex", alignItems: "flex-start", gap: 10 }}>
          <div style={{ fontSize: 22, flexShrink: 0, marginTop: 1 }}>{todaysTip.emoji}</div>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
              <div style={{ background: tipColors.tag, borderRadius: 20, padding: "1px 8px", fontSize: 10, fontWeight: 900, color: tipColors.tagText }}>
                {CAT_LABELS[todaysTip.category]}
              </div>
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: tipColors.text, lineHeight: 1.45 }}>{todaysTip.tip}</div>
          </div>
          <button onClick={dismissTip} style={{ background: "none", border: "none", fontSize: 16, color: tipColors.text, opacity: 0.5, cursor: "pointer", padding: "0 2px", flexShrink: 0, lineHeight: 1 }}>✕</button>
        </div>
      )}

      {/* Hero */}
      <div style={{ background: heroGradient ?? "linear-gradient(135deg,#fbbf24 0%,#f97316 50%,#ec4899 100%)", padding: "22px 18px 20px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -30, right: -20, fontSize: 120, opacity: 0.1, transform: "rotate(15deg)" }}>💰</div>
        <div style={{ position: "absolute", bottom: -15, left: 5, fontSize: 80, opacity: 0.1 }}>🌟</div>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
          {tierMascot && (
            <div style={{ fontSize: 56, lineHeight: 1, animation: "float 3s infinite", flexShrink: 0, marginTop: 4 }}>{tierMascot}</div>
          )}
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 900, color: "#fff9", letterSpacing: 3, textTransform: "uppercase" as const, marginBottom: 2 }}>Welcome to</div>
            <div style={{ fontSize: 32, fontWeight: 900, color: "#fff", fontFamily: "Fredoka One", lineHeight: 1, marginBottom: 4 }}>{heading}</div>
            {tierMascot && (
              <div style={{ fontSize: 12, color: "#ffffffcc", fontWeight: 700, marginBottom: 4 }}>Your guide: {tierMascotName} 👋</div>
            )}
            {equippedTitle && (
              <div style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "#ffffff25", borderRadius: 20, padding: "3px 10px", marginBottom: 6 }}>
                <span style={{ fontSize: 13 }}>{equippedTitle.emoji}</span>
                <span style={{ fontSize: 11, fontWeight: 900, color: "#fff" }}>{equippedTitle.name}</span>
              </div>
            )}
            <div style={{ fontSize: 13, color: "#ffffffcc", fontWeight: 700, marginBottom: 14 }}>{sub}</div>
          </div>
        </div>
        <div style={{ background: "#ffffff35", borderRadius: 20, height: 12, overflow: "hidden", marginBottom: 5 }}>
          <div style={{ height: "100%", background: "#fff", borderRadius: 20, width: `${totalLessons > 0 ? (done / totalLessons) * 100 : 0}%`, transition: "width 0.7s" }} />
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontSize: 12, color: "#ffffffcc", fontWeight: 800 }}>{done}/{totalLessons} lessons complete 🌍</div>
          {highestBadge && (
            <div style={{ background: "#ffffff30", borderRadius: 20, padding: "3px 10px", display: "flex", alignItems: "center", gap: 5 }}>
              <span style={{ fontSize: 14 }}>{highestBadge.emoji}</span>
              <span style={{ fontSize: 10, fontWeight: 900, color: "#fff" }}>{highestBadge.label}</span>
            </div>
          )}
        </div>
      </div>

      <div style={{ padding: "14px 16px 0", display: "flex", flexDirection: "column", gap: 12 }}>

        {dailyEvent?.type === "broken" && (
          <div style={{ background: "#fff7ed", border: "2.5px solid #fb923c", borderRadius: 18, padding: "14px 16px" }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 10 }}>
              <div style={{ fontSize: 28 }}>💔</div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 900, color: "#9a3412", fontFamily: "Fredoka One" }}>Streak broken</div>
                <div style={{ fontSize: 13, color: "#c2410c", fontWeight: 700, marginTop: 2 }}>
                  You had a {dailyEvent.streak}-day streak! That's still great — start a new one today 💪
                </div>
              </div>
            </div>
            <button onClick={onDismissDailyEvent}
              style={{ width: "100%", background: "linear-gradient(135deg,#fb923c,#ea580c)", border: "none", borderRadius: 12, padding: "9px", color: "#fff", fontWeight: 900, fontSize: 13, cursor: "pointer", fontFamily: "Nunito" }}>
              Let's go! 🔥
            </button>
          </div>
        )}

        {dailyEvent?.type === "shielded" && (
          <div style={{ background: "#f0fdf4", border: "2.5px solid #16a34a", borderRadius: 18, padding: "14px 16px" }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 10 }}>
              <div style={{ fontSize: 28, animation: "bounce 2s infinite" }}>🛡️</div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 900, color: "#14532d", fontFamily: "Fredoka One" }}>Streak shield activated!</div>
                <div style={{ fontSize: 13, color: "#166534", fontWeight: 700, marginTop: 2 }}>
                  Your {dailyEvent.streak}-day streak is safe! You get one free miss per week — shield recharges next week.
                </div>
              </div>
            </div>
            <button onClick={onDismissDailyEvent}
              style={{ width: "100%", background: "linear-gradient(135deg,#16a34a,#15803d)", border: "none", borderRadius: 12, padding: "9px", color: "#fff", fontWeight: 900, fontSize: 13, cursor: "pointer", fontFamily: "Nunito" }}>
              Nice! 🛡️
            </button>
          </div>
        )}

        {dailyEvent?.type === "milestone" && (
          <div style={{ background: "linear-gradient(135deg,#fef9c3,#fef3c7)", border: "2.5px solid #fbbf24", borderRadius: 18, padding: "14px 16px" }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 10 }}>
              <div style={{ fontSize: 34, animation: "bounce 2s infinite" }}>{dailyEvent.badgeEmoji}</div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 900, color: "#78350f", fontFamily: "Fredoka One" }}>
                  {dailyEvent.streak}-day streak milestone! 🎉
                </div>
                <div style={{ fontSize: 13, color: "#92400e", fontWeight: 700, marginTop: 2 }}>
                  You earned the <strong>{dailyEvent.badgeLabel}</strong> badge. Keep it going!
                </div>
              </div>
            </div>
            <button onClick={onDismissDailyEvent}
              style={{ width: "100%", background: "linear-gradient(135deg,#fbbf24,#f97316)", border: "none", borderRadius: 12, padding: "9px", color: "#fff", fontWeight: 900, fontSize: 13, cursor: "pointer", fontFamily: "Nunito" }}>
              Awesome! 🏆
            </button>
          </div>
        )}

        {doneToday ? (
          <div style={{ background: "#dcfce7", border: "2.5px solid #16a34a", borderRadius: 18, padding: "12px 16px", display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ fontSize: 30 }}>✅</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 900, color: "#15803d", fontFamily: "Fredoka One" }}>Daily Challenge Done!</div>
              <div style={{ fontSize: 12, color: "#166534", fontWeight: 700 }}>
                🔥 {dailyData.streak} day streak — come back tomorrow!
              </div>
            </div>
            {highestBadge && <div style={{ fontSize: 24 }}>{highestBadge.emoji}</div>}
          </div>
        ) : (
          todaysQ && <DailyChallengeCard question={todaysQ} streak={dailyData?.streak ?? 0} earnedBadges={earnedBadges} onComplete={onChallengeComplete} />
        )}

        {worlds.map((w, idx) => {
          const doneCount = w.lessons.filter((_: any, i: number) => progress[`${w.id}-${i}`]).length;
          const pct = doneCount / w.lessons.length;
          const complete = pct === 1;
          const locked = getWorldLocked(worlds, idx, progress);
          return (
            <button key={w.id}
              onClick={() => !locked && onSelect(w.id)}
              className={locked ? undefined : "btn-bounce"}
              style={{
                background: locked ? "#f1f5f9" : "#fff",
                border: `3px solid ${locked ? "#cbd5e1" : w.color}`,
                borderRadius: 22, padding: 0,
                cursor: locked ? "default" : "pointer",
                textAlign: "left", overflow: "hidden",
                boxShadow: locked ? "none" : `0 6px 20px ${w.color}33`,
                transition: "box-shadow 0.2s,transform 0.2s",
                opacity: locked ? 0.65 : 1,
              }}>
              <div style={{ background: locked ? "#f8fafc" : `linear-gradient(135deg,${w.color}18,${w.bg})`, padding: "14px 16px" }}>
                <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
                  <div style={{ fontSize: 46, lineHeight: 1, animation: (!complete && !locked) ? "float 3s infinite" : "none", filter: locked ? "grayscale(1)" : "none" }}>{w.emoji}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
                      <div style={{ fontSize: 19, fontWeight: 900, color: locked ? "#94a3b8" : w.dark, fontFamily: "Fredoka One" }}>{w.label}</div>
                      <div style={{ fontSize: 22 }}>{locked ? "🔒" : complete ? "⭐" : ""}</div>
                    </div>
                    {locked ? (
                      <div style={{ fontSize: 12, color: "#94a3b8", fontWeight: 700 }}>
                        {lockHint(worlds, idx, progress)}
                      </div>
                    ) : (
                      <>
                        <div style={{ fontSize: 12, color: w.color, fontWeight: 800, marginBottom: 8 }}>{w.tagline}</div>
                        <div style={{ background: "#e5e7eb", borderRadius: 20, height: 9, overflow: "hidden" }}>
                          <div style={{ width: `${pct * 100}%`, height: "100%", background: `linear-gradient(90deg,${w.color},${w.dark})`, borderRadius: 20, transition: "width 0.6s" }} />
                        </div>
                        <div style={{ marginTop: 4, fontSize: 11, color: w.color, fontWeight: 800 }}>{doneCount}/{w.lessons.length} lessons</div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
